import {useContext, useEffect, useMemo, useState} from "react";
import {Operation} from "../../../../../utils/Enum.types";
import {Title} from "../../../../../stories/Title/Title";
import {TableHeaderProps} from "../../../../../utils/types";
import {TABLE_ROW_HEIGHT} from "../../../../../styles/Variables";
import {GridAlignment, GridColDef} from "@mui/x-data-grid-pro";
import Edit from "../../../../../images/EditProfile.svg";
import {StyledDatagrid} from "../../../../../styles/DatagridStyles";
import DeleteModal from "../../../../../customComponents/DeleteModal";
import {GetItemGroups} from "./queries/queries";
import {
  AtsItemGroupEdge,
  AtsItemGroupOrderField,
  DeleteItemGroupByIdResult,
  DeleteItemGroupByIdVars,
  GetItemGroupsResult,
  GetItemGroupsVars,
} from "./queries/types";
import {useLazyQuery, useMutation} from "@apollo/client";
import {FETCH_MORE_DATA, ROWS_PER_PAGE, debouncedDelay} from "../../../../../utils/constants";

import useUrlParams from "../../../../../customHooks/useUrlParams";
import {OrderDirection} from "../../../../../utils/commonQueryTypes";
import {AppContext} from "../../../../../context/context";
import {payloadTypes} from "../../../../../context/reducer";
import {DeleteItemGroup} from "./queries/mutation";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import Delete from "../../../../../images/Delete.svg";
import LoadingModal from "../../../../pages/LoadingModal";
import {ListPageProps} from "./Grouping";
import useAssetTableJson from "../../../../../json/useAssetTableJson";
import { useDebounce } from "../../../../../customHooks/useDebounce";

const List = ({setOperation, setMessage}: ListPageProps) => {
  const {customer_id} = useUrlParams();
  const [searchData, setSearchData] = useState("");
  const debouncedSearch = useDebounce(searchData, debouncedDelay);
  const {state, dispatch} = useContext(AppContext);

  const groupInput = document.getElementsByName("groupName");
  const [GetItemGroupsDetails, {data, loading, fetchMore}] = useLazyQuery<
    GetItemGroupsResult,
    GetItemGroupsVars
  >(GetItemGroups);
  const {user_details} = useLoggedInUserDetails();
  const {Masters_Table} = useAssetTableJson();

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [itemGroups, setItemGroups] = useState<AtsItemGroupEdge[]>([]);
  const [deleteModal, setDeleteModal] = useState(false);

  const [DeleteItemGroupDetails, {loading: deleteLoading}] = useMutation<
    DeleteItemGroupByIdResult,
    DeleteItemGroupByIdVars
  >(DeleteItemGroup, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const dynamicHeaders: TableHeaderProps[] =
    Masters_Table.Masters.Grouping.List.map((header) => ({
      headerName: header.headerName,
      className: header.cellClassName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      hideable: header.hideable,
      renderCell: (params) => {
        if (params.field === "actions") {
          return (
            <>
              <img
                src={Edit}
                alt=""
                onClick={() => {
                  dispatch({
                    type: payloadTypes.SET_GROUP_ID,
                    payload: {
                      groupId: params.row.groupID,
                    },
                  });
                  if (groupInput.length) groupInput[0].focus();
                  setOperation(Operation.UPDATE);
                }}
              />
              <img
                src={Delete}
                alt=""
                onClick={() => {
                  dispatch({
                    type: payloadTypes.SET_GROUP_ID,
                    payload: {
                      groupId: params.row.groupID,
                    },
                  });
                  setDeleteModal(!deleteModal);
                }}
              />
            </>
          );
        } else return params.row.value;
      },
    }));
  const columns: GridColDef[] = [...dynamicHeaders];

  const HandleDelete = async () => {
    try {
      setDeleteModal(!deleteModal);
      DeleteItemGroupDetails({
        variables: {
          itemGrpID: state.groupId,
          user_details,
        },
        refetchQueries: [
          {
            query: GetItemGroups,
            variables: {
              after: null,
              customerID: customer_id,
              first: ROWS_PER_PAGE,
              orderBy: {
                direction: OrderDirection.ASC,
                field: AtsItemGroupOrderField.GROUP_NAME,
              },

              name: debouncedSearch,
            },
          },
        ],
      }).then(({data}) => {
        if (data) {
          setMessage({
            flag: true,
            message: "Category Deleted Sucessfully",
            operation: Operation.DELETE,
          });
        }
      });
    } catch (err) {
      console.error((err as Error).message);
    }
  };
  const handleScroll = () => {
    if (hasNextPage && !loading) {
      fetchMore({
        variables: {
          first: FETCH_MORE_DATA,
          after: endCursor,
        },
        updateQuery: (prevResult, {fetchMoreResult}) => {
          if (!fetchMoreResult) return prevResult;

          const newEdges = fetchMoreResult.GetItemGroups.edges;
          const pageInfo = fetchMoreResult.GetItemGroups.pageInfo;
          setEndCursor(pageInfo.endCursor);
          setHasNextPage(pageInfo.hasNextPage);

          const duplicateCheck = prevResult.GetItemGroups.edges.filter(
            ({node: {id}}) =>
              newEdges.findIndex(({node: {id: newId}}) => newId === id) !== -1
          );

          if (duplicateCheck.length > 0) return prevResult;

          return {
            GetItemGroups: {
              edges: [...itemGroups, ...newEdges],
              pageInfo,
              totalCount: data ? data.GetItemGroups.totalCount! : 0,
            },
          };
        },
      });
    }
  };

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetItemGroups.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = itemGroups.find(
            (sub) => sub.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setItemGroups(updatedNewData);
      } else {
        setItemGroups(newData);
      }

      setEndCursor(data.GetItemGroups.pageInfo.endCursor);
      setHasNextPage(data.GetItemGroups.pageInfo.hasNextPage);
    } // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    if (customer_id) {
      GetItemGroupsDetails({
        variables: {
          after: null,
          customerID: customer_id,
          first: ROWS_PER_PAGE,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsItemGroupOrderField.GROUP_NAME,
          },
          name: debouncedSearch,
        },
      });
    }
  }, [customer_id, GetItemGroupsDetails, debouncedSearch]);
  const rows = useMemo(() => {
    return data
      ? data.GetItemGroups.edges.map(({node}, index) => ({
          id: index + 1,
          group_name: node.groupName,
          groupID: node.id,
        }))
      : [];
  }, [data]);

  return (
    <>
      <div className="master-data__list">
        <Title variant="subtitle1">{`List of Grouping`}</Title>
        <div className="master-data__list--tableblock">
          <StyledDatagrid
            columns={columns}
            rows={rows}
            rowHeight={TABLE_ROW_HEIGHT}
            disableRowSelectionOnClick
            hideFooter
            onRowsScrollEnd={handleScroll}
          />
        </div>
      </div>
      <DeleteModal
        id={state.categoryId}
        modalFlag={deleteModal}
        setModalFlag={setDeleteModal}
        handleDelete={HandleDelete}
      />
      <LoadingModal flag={deleteLoading || loading} />
    </>
  );
};

export default List;
